import React, { useState } from "react"
import { useHistory } from "react-router-dom"
import {
	Button,
	Typography,
	Grid,
	Container,
	CircularProgress,
	Link,
	IconButton,
	TextField,
	InputAdornment
} from "@material-ui/core"

import { Add, Search as SearchIcon, Close as CloseIcon } from "@material-ui/icons"

import {
	Divider,
	Loading,
	Notification,
	AlertContainer
} from "@/components"

import { MessageBlastData, MessageBlastStatus } from "@/protocols/messageBlast"

import useDidMount from "@/hooks/useDidMount"
import useValidation, { ErrorType } from "@/hooks/useValidation"

import Breadcrumb from "@/components/BreadcrumbNew"
import ChipHeatingDialog from "@/components/ChipHeatingDialog"

import { messageBlastStatusTranslatedMapCamelCase } from "@/utils/messageBlast"
import { letalkLinks } from "@/utils/link"
import { useGlobalStateStore } from "@/store/GlobalState"

import noMessageBlastImg from "@/assets/images/messageBlast/no_message_blast.svg"
import ApiService from "@/services/Api"
import ErrorHandler from "@/services/ErrorHandler"

import MessageBlastListItem from "@/pages/Admin/MessageBlast/MessageBlastListItem"
import MessageBlastSkeleton from "@/skeletons/Admin/MessageBlast"
import MessageBlastGroupPermissionGate from "@/components/PermissionGate/MessageBlastGroupPermissionGate"

import useStyles from "@/pages/Admin/MessageBlast/styles"
import useDebounce from "@/hooks/useDebounce"

export type MessageBlastsGroupedByStatus = Record<MessageBlastStatus, MessageBlastData[]>

export type MessageBlastSearchQuery = {
	messageBlastTitle: string
}

const MessageBlastList: React.FC = () => {
	const classes = useStyles()
	const history = useHistory()
	const globalStateStore = useGlobalStateStore()
	const isWabaChannel = globalStateStore.currentChannel?.channelType === "waba"

	const [loading, setLoading] = useState(true)
	const [creatingMessageBlast, setCreatingMessageBlast] = useState(false)
	const [messageBlasts, setMessageBlasts] = useState<MessageBlastsGroupedByStatus>()
	const [hasMoreThanOne, setHasMoreThanOne] = useState(false)
	const [messageBlastStatusListSorted, setMessageBlastStatusListSorted] = useState<Partial<MessageBlastStatus[]>>([])
	const [openChipHeating, setOpenChipHeating] = useState(false)
	const [searchQuery, setSearchQuery] = useState<MessageBlastSearchQuery>()

	const handleSearchChange = (searchText: string) => {
		setSearchQuery({ messageBlastTitle: searchText })
	}

	const { validation } = useValidation()

	const loadMessageBlasts = async () => {
		try {
			const response = await ApiService.get("/message-blasts", {
				params: {
					type: "user",
					message_blast_title: searchQuery?.messageBlastTitle
				}
			})

			const messageBlastsGroupedByStatus = response.data.messageBlastsGroupedByStatus as MessageBlastsGroupedByStatus

			const messageBlastStatusKeyListSorted = Object.keys(messageBlastsGroupedByStatus).sort() as MessageBlastStatus[]

			setMessageBlastStatusListSorted(messageBlastStatusKeyListSorted)

			messageBlastStatusKeyListSorted.forEach((messageBlastStatusKey) => {
				const possibleMessageBlastList = messageBlastsGroupedByStatus[messageBlastStatusKey]

				if (possibleMessageBlastList.length > 0) {
					setHasMoreThanOne(true)
				}
			})

			setMessageBlasts(messageBlastsGroupedByStatus)
		} catch (err) {
			ErrorHandler.handle(err as ErrorType)
		}
	}

	const handleOpenMessageBlastManagement = (messageBlastId: number) => {
		history.push(`/admin/automation/message-blast/${messageBlastId}`)
	}

	const handleCreateMessageBlast = async () => {
		setCreatingMessageBlast(true)

		const payload = {
			title: "Novo envio",
			type: "user"
		}

		try {
			const response = await ApiService.post("/message-blasts", payload)
			const messageBlastId = response.data.messageBlast.id
			Notification.success({ message: "Envio criado com sucesso!" })
			handleOpenMessageBlastManagement(messageBlastId)
		} catch (err) {
			ErrorHandler.handle(err as ErrorType)
			Notification.error({ message: "Não foi possível criar o envio!" })
			setCreatingMessageBlast(false)
		}
	}

	const setup = async () => {
		await Promise.all([
			loadMessageBlasts()
		])

		setLoading(false)
	}

	const handleOpenChipHeating = () => {
		setOpenChipHeating(true)
	}
	const handleCloseChipHeating = () => {
		setOpenChipHeating(false)
	}
	const handleSaveChipHeating = () => {
		handleCloseChipHeating()
		handleCreateMessageBlast()
	}

	useDebounce(
		async () => {
			loadMessageBlasts()
		},
		searchQuery?.messageBlastTitle,
		2250
	)

	useDidMount(() => {
		setup()
	})

	return (
		<Loading
			loading={loading}
			customLoadingElement={<MessageBlastSkeleton />}
		>
			<>
				{hasMoreThanOne ? (
					<Grid container>
						<Breadcrumb
							data={[
								{ name: "Envios em Massa", pathname: "/admin/automation/message-blast" }
							]}
						/>
						<Divider orientation="horizontal" size={3} />

						<Grid container direction="row" justifyContent="space-between" alignItems="center">
							<Grid item>
								<MessageBlastGroupPermissionGate action="create">
									<Button
										variant="contained"
										color="primary"
										onClick={handleCreateMessageBlast}
										endIcon={creatingMessageBlast && <CircularProgress size={20} />}
										disabled={creatingMessageBlast}
										startIcon={<Add/>}
									>
										Adicionar Envio
									</Button>
								</MessageBlastGroupPermissionGate>
							</Grid>
							<Grid item>
								<TextField
									value={searchQuery?.messageBlastTitle}
									onChange={({ target }) => handleSearchChange(target.value)}
									placeholder="Pesquisar"
									variant="outlined"
									InputProps={{
										startAdornment: (
											<InputAdornment style={{ minHeight: 52 }} position="start">
												<SearchIcon />
											</InputAdornment>
										),
										endAdornment: searchQuery?.messageBlastTitle && (
											<IconButton
												size="small"
												onClick={() => handleSearchChange("")}
											>
												<CloseIcon
													fontSize="small"
												/>
											</IconButton>
										)
									}}
									fullWidth
									size="small"
									helperText={validation.search}
									error={!!validation.search}
								/>
							</Grid>
						</Grid>

						<Divider orientation="horizontal" size={5} />

						{isWabaChannel && (
							<>
								<Grid item xs={12}>
									<AlertContainer
										alertType="info"
										title="Envio em massa na WABA"
									>
										<Typography variant="body2">
											<Link
												href={letalkLinks.wikiWabaMessageBlastHowItWorksAndBestPractices}
												underline="always"
												target="_blank"
												color="inherit"
											>
												Clique aqui
											</Link>
											{" "}para entender como funciona o envio em massa na WABA e{" "}
											<Link
												href={letalkLinks.wikiWabaMessageBlastSafeSendingBestPractices}
												underline="always"
												target="_blank"
												color="inherit"
											>
												aqui
											</Link>
											{" "}para ver as boas práticas de envios seguros.
										</Typography>
									</AlertContainer>
								</Grid>

								<Divider orientation="horizontal" size={5} />
							</>
						)}

						<Grid item xs={11} style={{ minWidth: "100%" }}>
							<Grid container spacing={7} direction="column">
								{messageBlastStatusListSorted.map((messageBlastStatusKey) => {
									return <Grid item xs key={messageBlastStatusKey}>
										{(messageBlastStatusKey && messageBlasts) && <Grid container>
											<Typography
												variant="body1"
												className={classes.coloredTitle}
											>
												ENVIOS PARA CONTATOS:
											</Typography>

											<Divider orientation="horizontal" size={1}/>

											<Typography variant="h4" className={classes.messageBlastStatusName}>
												{messageBlastStatusTranslatedMapCamelCase[messageBlastStatusKey]}
											</Typography>

											<Divider orientation="horizontal" size={3}/>
											{
												messageBlasts[messageBlastStatusKey] && messageBlasts[messageBlastStatusKey].map(msg => (<Grid
													item
													xs={12}
													key={msg.id}
												>
													<MessageBlastListItem
														title={msg.title}
														status={msg.status}
														createdAt={new Date(msg.createdAt)}
														sentAt={msg.status === "SENT" ? new Date(msg.statusDate) : undefined}
														messageBlastId={msg.id}
														scheduleTime={msg.schedule?.time}
														createdByUser={msg.createdByUser}
														executedByUser={msg.executedByUser}
													/>
													<Divider orientation="horizontal" size={2}/>
												</Grid>))
											}
										</Grid>}
									</Grid>
								})}
							</Grid>
						</Grid>
					</Grid>
				) : (
					<Container maxWidth="md">
						<Grid
							container
							alignItems="center"
							justify="space-between"
							direction="column"
							spacing={8}
						>
							<Grid item>
								<img
									src={noMessageBlastImg}
									alt="Sem envios"
									className={classes.noMessageBlastImage}
								/>
							</Grid>

							<Grid item xs={10}>
								<Typography
									variant="h5"
									className={classes.noMessageBlastTitle}
									align="center"
								>
									Faça um envio simples e rápido agora mesmo
								</Typography>
							</Grid>

							<Grid xs={8} alignItems="center">
								<Typography
									variant="body1"
									className={classes.noMessageBlastText}
									align="center"
								>
									Envie mensagens para centenas de contatos ao mesmo tempo! Comece a conversa com seus leads
									e clientes em um canal de diálogo direto e com mais chances de visualizações e conversões.
								</Typography>

							</Grid>

							<Grid>
								<Divider orientation="horizontal" size={3} />
								{isWabaChannel ? (
									<Typography variant="body2" align="center">
										<Link
											href={letalkLinks.wikiWabaMessageBlastHowItWorksAndBestPractices}
											underline="always"
											target="_blank"
											color="inherit"
										>
											Clique aqui
										</Link>
										{" "}para entender como funciona o envio em massa na WABA e{" "}
										<Link
											href={letalkLinks.wikiWabaMessageBlastSafeSendingBestPractices}
											underline="always"
											target="_blank"
											color="inherit"
										>
											aqui
										</Link>
										{" "}para ver as boas práticas de envios seguros.
									</Typography>
								) : (
									<Link
										href={letalkLinks.wikiHowToCreateMessageBlast}
										underline="always"
										target="_blank"
										color="inherit"
									>
										Como fazer o seu primeiro envio em massa para contatos
									</Link>
								)}
							</Grid>

							<Divider orientation="horizontal" size={2} />

							<Grid item>
								<MessageBlastGroupPermissionGate action="create">
									<Button
										variant="contained"
										color="primary"
										onClick={handleOpenChipHeating}
										endIcon={creatingMessageBlast && <CircularProgress size={20} />}
										disabled={creatingMessageBlast}
										startIcon={<Add/>}
									>
										ADICIONAR O PRIMEIRO ENVIO
									</Button>
								</MessageBlastGroupPermissionGate>
							</Grid>
						</Grid>
					</Container>
				)}
				<ChipHeatingDialog open={openChipHeating} onSave={handleSaveChipHeating} onClose={handleCloseChipHeating}/>
			</>
		</Loading>
	)
}

export default MessageBlastList
